import { Icon, cn } from "@nexus/ui";
import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { downloadScriptCustomAsset } from "@/features/marketplace/api/marketplace.api";
import { OriginalBriefLink } from "@/features/opportunities/components/scripts/OriginalBriefLink";
import { resolveOriginalBriefUrl } from "@/features/opportunities/lib/original-brief-url";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";

export interface ScriptCustomAssetRowLinkProps {
  script: {
    id: string;
    customAssetUploadId?: string | null;
    originalBriefUrl?: string | null;
    rationale?: string | null;
  };
  setId: string;
  scope: "opportunities" | "persona-opportunities";
  className?: string;
}

export function ScriptCustomAssetRowLink({
  script,
  setId,
  scope,
  className,
}: ScriptCustomAssetRowLinkProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const [downloading, setDownloading] = useState(false);
  const uploadId = script.customAssetUploadId?.trim();
  const briefUrl = !uploadId ? resolveOriginalBriefUrl(script) : null;

  if (uploadId) {
    const handleDownload = async (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!activeTenantId || downloading) return;
      setDownloading(true);
      try {
        await downloadScriptCustomAsset(scope, setId, script.id, activeTenantId);
      } catch (error) {
        toast.error(getUserFacingApiMessage(error, passThroughMessage));
      } finally {
        setDownloading(false);
      }
    };

    return (
      <span
        role="button"
        tabIndex={downloading ? -1 : 0}
        aria-disabled={downloading || undefined}
        onClick={(event) => void handleDownload(event)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            void handleDownload(event);
          }
        }}
        className={cn(
          "inline-flex items-center gap-1 text-[11px] font-medium text-primary underline-offset-2 hover:underline aria-disabled:opacity-60",
          downloading ? "cursor-wait" : "cursor-pointer",
          className,
        )}
        data-testid="script-custom-asset-download-link"
      >
        <Icon name="download" size="xs" className="shrink-0 text-current" aria-hidden />
        {downloading
          ? t("marketplace.customBrief.downloading", "Downloading…")
          : t("marketplace.customBrief.downloadAsset", "Download brief")}
      </span>
    );
  }

  if (briefUrl) {
    return (
      <span onClick={(event) => event.stopPropagation()} className={className}>
        <OriginalBriefLink url={briefUrl} className="text-[11px]" />
      </span>
    );
  }

  return null;
}
